import React, { Ref } from 'react';
import mergeRefs from 'react-merge-refs';

import { SporsmalHeader } from '../../components/sporsmal-header/sporsmal-header';
import { FocusableHtmlElement, useFocusRef } from '../hooks';
import { KontaktinfoInput } from '../kontaktinfo/kontaktinfo-field';
import { updateSimpleKontaktinfosvar } from '../reducer/sporsmalsflyt-actions';
import { useSporsmalsflytDispatch } from '../reducer/sporsmalsflytDispatchProvider';
import { CurrentSimpleKontaktinfosporsmal } from './simpleKontaktinfo-types';
import { getFeilmeldingFrom, isValid, SimpleKontaktinfofelt } from './simpleKontaktinfofelt';

const firstFieldWithError = (currentSporsmal: CurrentSimpleKontaktinfosporsmal): SimpleKontaktinfofelt | undefined => {
    const fieldOrder = [
        SimpleKontaktinfofelt.FulltNavn,
        SimpleKontaktinfofelt.Mobilnummer,
        SimpleKontaktinfofelt.Personnummer,
    ];
    return fieldOrder.find((field) => !isValid(currentSporsmal, field));
};

const isFirstFieldWithError = (
    field: SimpleKontaktinfofelt,
    currentSporsmal: CurrentSimpleKontaktinfosporsmal,
): boolean => field === firstFieldWithError(currentSporsmal);

interface Props {
    currentSporsmal: CurrentSimpleKontaktinfosporsmal;
    showFeilmeldinger: boolean;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
}

export const SimpleKontaktinfo = ({ currentSporsmal, showFeilmeldinger, errorRef, focusOnMount = true }: Props) => {
    const dispatch = useSporsmalsflytDispatch();

    const onChange = (value: string, felt: SimpleKontaktinfofelt) => {
        dispatch(updateSimpleKontaktinfosvar(felt, value));
    };

    const fornavnRef = useFocusRef<HTMLInputElement>(focusOnMount);

    return (
        <SporsmalHeader tittel={currentSporsmal.sporsmal.tittel} ingress={currentSporsmal.sporsmal.ingress}>
            <KontaktinfoInput
                name="navn"
                value={currentSporsmal.fulltNavn}
                label="Fullt navn"
                errorText={getFeilmeldingFrom(SimpleKontaktinfofelt.FulltNavn)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, SimpleKontaktinfofelt.FulltNavn)}
                onChange={(value) => {
                    onChange(value, SimpleKontaktinfofelt.FulltNavn);
                }}
                itemRef={mergeRefs([
                    fornavnRef,
                    isFirstFieldWithError(SimpleKontaktinfofelt.FulltNavn, currentSporsmal) ? errorRef : undefined,
                ])}
            />
            <KontaktinfoInput
                name="mobilnummer"
                value={currentSporsmal.mobilnummer}
                label="Telefonnummer"
                type="tel"
                errorText={getFeilmeldingFrom(SimpleKontaktinfofelt.Mobilnummer)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, SimpleKontaktinfofelt.Mobilnummer)}
                onChange={(value) => {
                    onChange(value, SimpleKontaktinfofelt.Mobilnummer);
                }}
                itemRef={
                    isFirstFieldWithError(SimpleKontaktinfofelt.Mobilnummer, currentSporsmal) ? errorRef : undefined
                }
                small
            />

            <KontaktinfoInput
                name="fnr"
                value={currentSporsmal.fodselsnummer}
                label="Personnummer"
                numericKeyboard
                errorText={getFeilmeldingFrom(SimpleKontaktinfofelt.Personnummer)}
                isValid={!showFeilmeldinger || isValid(currentSporsmal, SimpleKontaktinfofelt.Personnummer)}
                onChange={(value) => {
                    onChange(value, SimpleKontaktinfofelt.Personnummer);
                }}
                itemRef={
                    isFirstFieldWithError(SimpleKontaktinfofelt.Personnummer, currentSporsmal) ? errorRef : undefined
                }
                small
            />
        </SporsmalHeader>
    );
};
